import { useState } from 'react';
import { useDispatch } from 'react-redux';

import { LISTING_STATUS } from '../../../constants/listing.constants';
import { MENU_ACTION } from './listingCardMenu';
import { editLinkProps, viewLinkProps } from './listingCardLinks';
import { STOP_OUTCOME } from '../ConfirmationModal/StopListingModal';
import { stopAndCancelSubscription } from '../ManageListingsPage.duck';

export const DIALOG = {
  STOP: 'stop',
  SUBSCRIPTION: 'subscription',
};

/**
 * Binds the menu's action keys to what actually happens.
 *
 * Kept out of the card so the card renders and this decides. Note the deliberate
 * split between confirmed and unconfirmed actions: transitions reached through the
 * ⋯ menu dispatch directly, because the menu subtitle already stated the
 * consequence, and the stop dialog's outcomes dispatch directly too — the seller has
 * just read a two-column explanation and pressed a verb. Asking "are you sure?"
 * again after that is friction that teaches sellers to click through warnings.
 *
 * @param {Object} args
 * @param {Object} args.listing
 * @param {boolean} [args.isDetailsComplete] decides which step 'edit' opens on
 * @param {Function} args.onUpdateListingStatus direct dispatch (listingId, status, state)
 * @param {Function} args.onOpenListing
 * @param {Function} args.onCloseListing
 * @param {Function} args.onDiscardDraft
 */
export const useListingCardActions = ({
  listing,
  isDetailsComplete = false,
  onUpdateListingStatus,
  onOpenListing,
  onCloseListing,
  onDiscardDraft,
}) => {
  const dispatch = useDispatch();
  const [isMenuOpen, setMenuOpen] = useState(false);
  const [activeDialog, setActiveDialog] = useState(null);

  const listingId = listing?.id;
  const listingState = listing?.attributes?.state;

  const closeMenu = () => setMenuOpen(false);

  const openDialog = dialog => {
    closeMenu();
    setActiveDialog(dialog);
  };

  const handleMenuSelect = key => {
    closeMenu();

    switch (key) {
      case MENU_ACTION.NEGOTIATE:
        return onUpdateListingStatus(listingId, LISTING_STATUS.UNDER_NEGOTIATION, listingState);
      case MENU_ACTION.FOR_SALE:
        return onUpdateListingStatus(listingId, LISTING_STATUS.FOR_SALE, listingState);
      case MENU_ACTION.PUBLISH:
      case MENU_ACTION.REPUBLISH:
        return onOpenListing(listingId, listingState);
      case MENU_ACTION.STOP:
        return openDialog(DIALOG.STOP);
      // All three lead to the subscription dialog, which is where the plan, the
      // price and the resume/renew actions live.
      case MENU_ACTION.SUBSCRIPTION:
      case MENU_ACTION.RESUME:
      case MENU_ACTION.RENEW:
        return openDialog(DIALOG.SUBSCRIPTION);
      case MENU_ACTION.DELETE:
      case MENU_ACTION.DELETE_PAID:
        return onDiscardDraft(listingId);
      default:
        // edit and view are links, handled by linkPropsByKey.
        return undefined;
    }
  };

  const handleStopConfirm = outcome => {
    setActiveDialog(null);

    switch (outcome) {
      case STOP_OUTCOME.SOLD:
        // Archives the advertisement and cancels the subscription. If only the first
        // half succeeds the card says so — see subscriptionCancelError in the duck.
        return onUpdateListingStatus(listingId, LISTING_STATUS.SOLD, listingState);
      case STOP_OUTCOME.OFFLINE:
        return onCloseListing(listingId);
      case STOP_OUTCOME.CANCEL:
        return dispatch(stopAndCancelSubscription(listingId));
      default:
        return undefined;
    }
  };

  return {
    isMenuOpen,
    setMenuOpen,
    activeDialog,
    openDialog,
    closeDialog: () => setActiveDialog(null),
    handleMenuSelect,
    handleStopConfirm,
    linkPropsByKey: listing?.id
      ? {
          [MENU_ACTION.EDIT]: editLinkProps(listing, { isDetailsComplete }),
          [MENU_ACTION.VIEW]: viewLinkProps(listing),
        }
      : {},
  };
};
